import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Button, Popover, Progress} from "antd";
import styled from "styled-components";

const AcneDetailsComponent = ({
                                  original_image_url,
                                  detections_url,
                                  full_detail,
                                  visualization_url,
                                  created_at,
                                  onClick,
                              }) => {
    const {t} = useTranslation('common');
    const [showDetections, setShowDetections] = useState(true);
    const imageRef = useRef(null);
    const [imageDimensions, setImageDimensions] = useState({
        width: 0,
        height: 0,
        originalWidth: 0,
        originalHeight: 0
    });
    const [highlightedDetection, setHighlightedDetection] = useState(null);
    const [detectionData, setDetectionData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(detections_url);
                const result = await response.json();
                setDetectionData(result);
            } catch (error) {
                console.error("Error fetching detection results:", error);
            }
        };

        if (detections_url) {
            fetchData();
        }
    }, [detections_url]);

    useEffect(() => {
        if (imageRef.current) {
            const updateDimensions = () => {
                setImageDimensions({
                    width: imageRef.current.clientWidth,
                    height: imageRef.current.clientHeight,
                    originalWidth: imageRef.current.naturalWidth,
                    originalHeight: imageRef.current.naturalHeight
                });
            };

            // Set initial dimensions when image loads
            if (imageRef.current.complete) {
                updateDimensions();
            } else {
                imageRef.current.onload = updateDimensions;
            }

            // Add resize listener to handle container size changes
            const resizeObserver = new ResizeObserver(updateDimensions);
            resizeObserver.observe(imageRef.current);

            return () => {
                if (imageRef.current) {
                    resizeObserver.unobserve(imageRef.current);
                }
            };
        }
    }, [imageRef.current, showDetections]);

    const toggleDetections = () => {
        setShowDetections(!showDetections);
    };

    const getTagColor = (className) => {
        const colors = {
            'atrophic-hypertrophic_scar': 'orange',
            'melasma-nevus': '#5acd4b',
            'papule-pustule': '#bd47a1',
            'open-closed_comedo': '#73e5d5',
            'nodule-cyst': '#e55e73',
            'severe-acne-lesions': '#e5a273',
            'default': 'blue'
        };

        return colors[className] || colors.default;
    };

    const mapAcneDetail = (acneType) => {
        const acneDetails = {
            'open-closed_comedo': t("open_closed_comedo"),
            'papule-pustule': t("papule_pustule"),
            'nodule-cyst': t("nodule_cyst"),
            'severe-acne-lesions': t("severe_acne_lesions"),
            'melasma-nevus': t("melasma_nevus"),
            'atrophic-hypertrophic_scar': t("atrophic_hypertrophic_scar"),
        }

        return acneDetails[acneType] || t("unknown_acne_type");
    };

    const renderPopoverContent = (detection) => {
        return (
            <div>
                <h4 className="capitalize font-medium mb-2">{mapAcneDetail(detection.class_name)}</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>{t("confidence")}:</div>
                    <div>{(detection.score * 100).toFixed(1)}%</div>
                    <div>{t("width")}:</div>
                    <div>{(detection.bbox[2] - detection.bbox[0]).toFixed(1)}px</div>
                    <div>{t("height")}:</div>
                    <div>{(detection.bbox[3] - detection.bbox[1]).toFixed(1)}px</div>
                    <div>{t("location")}:</div>
                    <div>{t("x")}: {detection.bbox[0].toFixed(0)}, {t("y")}: {detection.bbox[1].toFixed(0)}</div>
                </div>
            </div>
        );
    };

    const renderDetectionOverlays = () => {
        if (!detectionData || !detectionData.detections) return null;

        const detections = detectionData.detections || [];
        if (!detections.length || !imageRef.current) return null;

        // Calculate aspect ratios
        const originalAspectRatio = imageDimensions.originalWidth / imageDimensions.originalHeight;
        const displayAspectRatio = imageDimensions.width / imageDimensions.height;

        // Determine if we need to account for letterboxing due to objectFit: 'cover'
        let scaleX, scaleY, offsetX = 0, offsetY = 0;

        if (displayAspectRatio > originalAspectRatio) {
            // Width is constrained, height may overflow
            const displayHeightIfFull = imageDimensions.width / originalAspectRatio;
            scaleX = imageDimensions.width / imageDimensions.originalWidth;
            scaleY = displayHeightIfFull / imageDimensions.originalHeight;
            // Calculate vertical overflow that gets cropped
            offsetY = (displayHeightIfFull - imageDimensions.height) / 2;
        } else {
            // Height is constrained, width may overflow
            const displayWidthIfFull = imageDimensions.height * originalAspectRatio;
            scaleY = imageDimensions.height / imageDimensions.originalHeight;
            scaleX = displayWidthIfFull / imageDimensions.originalWidth;
            // Calculate horizontal overflow that gets cropped
            offsetX = (displayWidthIfFull - imageDimensions.width) / 2;
        }

        return detections.map((detection, index) => {
            // Scale coordinates accounting for crop
            const x = detection.bbox[0] * scaleX - offsetX;
            const y = detection.bbox[1] * scaleY - offsetY;
            const width = (detection.bbox[2] - detection.bbox[0]) * scaleX;
            const height = (detection.bbox[3] - detection.bbox[1]) * scaleY;

            // Don't render if outside visible area
            if (x + width < 0 || y + height < 0 ||
                x > imageDimensions.width || y > imageDimensions.height) {
                return null;
            }

            const color = getTagColor(detection.class_name);
            const isHighlighted = highlightedDetection === index;

            // Calculate circle dimensions
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            const diagonal = Math.sqrt(width * width + height * height);
            // Use the larger dimension to ensure circle covers the whole detection
            const radius = diagonal / 2 + 5; // Add some padding

            return (
                <Popover
                    key={index}
                    content={renderPopoverContent(detection)}
                    title={`${t("detection")} #${index + 1}`}
                    trigger="hover"
                    placement="top"
                    open={isHighlighted}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: `${Math.max(0, centerX - radius)}px`,
                            top: `${Math.max(0, centerY - radius)}px`,
                            width: `${Math.min(radius * 2, imageDimensions.width - Math.max(0, centerX - radius))}px`,
                            height: `${Math.min(radius * 2, imageDimensions.height - Math.max(0, centerY - radius))}px`,
                            border: `2px solid ${color}`,
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            zIndex: isHighlighted ? 9 : 4,
                            cursor: 'pointer'
                        }}
                        onMouseEnter={() => setHighlightedDetection(index)}
                        onMouseLeave={() => setHighlightedDetection(null)}
                        onClick={() => setHighlightedDetection(isHighlighted ? null : index)}
                    />
                </Popover>
            );
        });
    };

    if (!detectionData) {
        return <div className="text-center p-4">{t("loading_data")}...</div>;
    }

    // Group detections by class
    const groupedDetections = detectionData.detections.reduce((acc, detection) => {
        const className = detection.class_name;
        if (!acc[className]) {
            acc[className] = [];
        }
        acc[className].push(detection);
        return acc;
    }, {});

    const legendContent = (
        <div>
            {Object.entries(groupedDetections).map(([className, items], idx) => (
                <div className="flex justify-between items-center mb-2" key={idx}>
                    <p className="capitalize text-xs">{mapAcneDetail(className)}</p>
                    <div className="rounded-full flex justify-center items-center" style={{
                        backgroundColor: getTagColor(className),
                        width: '18px',
                        height: '18px',
                    }}>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-3">
                <div className="text-center flex justify-between gap-3 rounded-xl overflow-hidden relative"
                     style={{minHeight: '15rem'}}>
                    <Popover content={legendContent} title={t("details")} className="absolute right-3 top-3"
                             placement="bottomRight" zIndex={10}>
                        <Button type="primary">{t("see_details")}</Button>
                    </Popover>
                    {showDetections && visualization_url ? (
                        <img
                            src={visualization_url}
                            ref={imageRef}
                            alt="Detection Visualization"
                            style={{objectFit: 'cover', width: '100%', height: '100%'}}
                        />
                    ) : (
                        <div className="relative w-full h-full">
                            <img
                                src={original_image_url}
                                ref={imageRef}
                                alt="Original Image"
                                style={{objectFit: 'cover', width: '100%', height: '100%'}}
                            />
                            {renderDetectionOverlays()}
                        </div>
                    )}
                </div>

                <Button type="primary" onClick={toggleDetections}>
                    {showDetections ? t("hide_detections") : t("show_detections")}
                </Button>
            </div>

            <LesionWrapper className="flex flex-col gap-3 justify-between items-start bg-white p-3 rounded-xl">
                <h3 className="font-bold text-center text-black w-full">{t("skin_lesion_analysis")}</h3>

                {Object.keys(groupedDetections).length > 0 ? (
                    <>
                        <div className="w-full">
                            <h4 className="font-semibold mb-2">{t("detected_lesions")}</h4>
                            <div className="m-3">
                                {Object.entries(groupedDetections).map(([className, items], idx) => (
                                    <div className="flex justify-between items-center mb-2" key={idx}>
                                        <h3 className="capitalize">{mapAcneDetail(className)}</h3>
                                        <div className="rounded-full flex justify-center items-center" style={{
                                            backgroundColor: getTagColor(className),
                                            width: '50px',
                                            height: '50px',
                                        }}>
                                            <p style={{
                                                color: "white",
                                                fontSize: '24px',
                                            }}>{items.length}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-6">
                                {Object.entries(groupedDetections).map(([className, items], idx) => {
                                    const avgConfidence = items.reduce((sum, item) => sum + item.score, 0) / items.length;
                                    return (
                                        <div className="flex flex-col items-center" key={idx}>
                                            <Progress
                                                className="light-progress"
                                                type="circle"
                                                percent={(avgConfidence * 100).toFixed(1)}
                                                strokeColor={getTagColor(className)}
                                                format={(percent) => `${percent}%`}
                                            />
                                            <div className="text-center mt-2">
                                                <h3 className="capitalize">{mapAcneDetail(className)}</h3>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="w-full text-center p-4 bg-gray-50 rounded-lg">
                        {t("no_lesions_detected")}
                    </div>
                )}
            </LesionWrapper>
        </div>
    );
};

const LesionWrapper = styled.div`
    * {
        color: black;
    }

    table {
        border-collapse: collapse;
    }

    table td, table th {
        border: 1px solid #e2e8f0;
    }
`

export default AcneDetailsComponent;